import { ReactNode, useMemo } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useCotiPluginContext } from "@/context/PrivacyBridgeContext";
import { useAccount as useWagmiAccount } from "wagmi";
import { Unplug } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getNetworkNameForChain, getChainConfig } from "@/chains";

interface NetworkGuardProps {
    children: ReactNode;
}

const pseudoRandom = (seed: number) => ((seed * 9301 + 49297) % 233280) / 233280;

const PARTICLES = [...Array(30)].map((_, i) => ({
    id: i,
    left: `${pseudoRandom(i + 1) * 100}%`,
    top: `${pseudoRandom(i + 31) * 100}%`,
    x: (pseudoRandom(i + 61) - 0.5) * 50,
    duration: 5 + pseudoRandom(i + 91) * 5,
    delay: pseudoRandom(i + 121) * 5,
}));

const NetworkGuard = ({ children }: NetworkGuardProps) => {
    const { chainId, switchNetwork } = useCotiPluginContext();
    const { isConnected: wagmiConnected, chainId: wagmiChainId } = useWagmiAccount();

    const envDefaultNetwork = import.meta.env.VITE_DEFAULT_NETWORK_ID;

    const isBlocking = useMemo(() => {
        if (!envDefaultNetwork || !chainId) return false;

        // Never enforce network when wallet is disconnected
        if (!wagmiConnected) return false;

        // If connected via RainbowKit/wagmi to a supported chain, skip enforcement
        if (wagmiConnected && wagmiChainId && getChainConfig(wagmiChainId)) {
            return false;
        }

        let currentChainIdHex = "";
        try {
            currentChainIdHex = "0x" + BigInt(chainId).toString(16);
        } catch {
            currentChainIdHex = chainId.startsWith("0x") ? chainId : "0x" + Number(chainId).toString(16);
        }

        const allowedChainIdHex = envDefaultNetwork.toLowerCase();

        const isMismatch = currentChainIdHex.toLowerCase() !== allowedChainIdHex;
        if (isMismatch) {
            console.warn("[NetworkGuard] Mismatch Detected!");
        }
        return isMismatch;
    }, [envDefaultNetwork, chainId, wagmiConnected, wagmiChainId]);

    if (!isBlocking) {
        return <>{children}</>;
    }

    const handleSwitch = async () => {
        if (envDefaultNetwork) {
            await switchNetwork(envDefaultNetwork);
        }
    };

    const getNetworkName = (id: string | undefined) => {
        if (!id) return "Unknown Network";
        return getNetworkNameForChain(BigInt(id).toString());
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />

            {/* Background particles */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                {PARTICLES.map(particle => (
                    <motion.div
                        key={particle.id}
                        className="absolute w-1 h-1 rounded-full bg-primary/40"
                        style={{
                            left: particle.left,
                            top: particle.top,
                        }}
                        animate={{
                            y: [-20, -120],
                            x: [0, particle.x],
                            opacity: [0, 0.8, 0],
                            scale: [0, 1, 0],
                        }}
                        transition={{
                            duration: particle.duration,
                            repeat: Infinity,
                            delay: particle.delay,
                            ease: "easeOut",
                        }}
                    />
                ))}
            </div>

            <main className="flex-1 flex items-center justify-center px-6 relative z-10">
                <div className="rounded-[24px] glass-panel p-6 md:p-12 max-w-md w-full text-center relative overflow-hidden shadow-2xl">
                    <div className="w-20 h-20 rounded-full bg-destructive/20 border-2 border-destructive/50 flex items-center justify-center mx-auto mb-6">
                        <Unplug className="h-10 w-10 text-destructive" />
                    </div>

                    <h2 className="text-2xl font-bold text-foreground mb-2">Wrong Network</h2>
                    <p className="text-sm text-muted-foreground mb-8">
                        You are connected to the wrong network. <br />
                        Please switch to <strong>{getNetworkName(envDefaultNetwork)}</strong> to continue.
                    </p>

                    <Button variant="destructive" className="w-full relative z-10" onClick={handleSwitch}>
                        Switch Network
                    </Button>

                    {/* Background Glows */}
                    <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />
                    <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2 pointer-events-none" />
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default NetworkGuard;
